<template>
  <div class="course-video">
    <div class="course-video__details">
      <div v-if="!isLoading" class="toggle-icons">
        <div class="course-video__icon-wrapper">
          <VideoIcon
            :fill="hasWatched ? '#00DEA0' : '#808080'"
            class="course-video__icon"
          />
        </div>
        <div class="toggle-icons__check-wrapper">
          <CheckIcon
            :fill="hasWatched ? '#00DEA0' : '#727276'"
            class="toggle-icons__check"
          />
          <button
            class="toggle-icons__mark-as-finish"
            @click="toggleVideoStatus()"
          >
            <span v-if="!hasWatched">Mark as finished</span>
            <span v-else>Mark as unfinished</span>
          </button>
        </div>
        <div class="course-video__report-issue" @click.stop="startIssue">
          <IssueIcon class="course-video__report-issue__icon" />
          <span class="course-video__report-issue__text">Report an issue</span>
        </div>
      </div>
      <div v-else class="course-video__loading-wrapper">
        <ActionLoader
          loaderClass="course-video__loading-spinner"
          name="ball-clip-rotate"
        />
      </div>
      <div class="course-video__duration-wrapper" v-if="video.duration">
        <p class="course-video__duration">
          {{ video.duration }}
        </p>
      </div>
    </div>
    <div v-if="isWatching" class="course-video__player-wrapper">
      <MediaDelegate
        v-bind="{
          parentType: 'course',
          content: { ...video, type: 'video' },
          nextMedia: nextContent,
          userCompletionStatus: videoStatus,
        }"
        ref="mediaDelegate"
        @advance-media="playNextContent"
        @is-loading="updateLoader"
      />
      <Files
        class="video-files"
        :content="video"
        :canOpen="false"
        :transparent="true"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import VideoIcon from '@/assets/icons/course/video_icon.svg'
import MediaDelegate from '@/components/media/MediaDelegate.vue'
import Files from '@/components/modals/exercise-modal/Files.vue'
import CheckIcon from '@/assets/icons/course/exercise/check-circle.svg'
import IssueIcon from '@/assets/icons/course/bug.svg'
import CONSTANTS from '@/constants'

export default {
  name: 'CourseVideo',
  components: {
    VideoIcon: { ...VideoIcon, name: 'VideoIcon' },
    ActionLoader,
    MediaDelegate,
    Files,
    CheckIcon: { ...CheckIcon, name: 'CheckIcon' },
    IssueIcon: { ...IssueIcon, name: 'IssueIcon' },
  },
  data() {
    return {
      isLoading: false,
    }
  },
  props: {
    video: {
      type: Object,
      required: true,
    },
    isWatching: {
      type: Boolean,
      required: true,
    },
    videoStatus: {
      type: String,
      required: true,
    },
    quizId: {
      type: String,
      required: false,
    },
    hasPassedQuiz: {
      type: Boolean,
      required: false,
    },
    nextVideo: {
      type: [Object, null],
      required: false,
    },
    courseId: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState({
      profile: state => state.auth.userProfile,
    }),
    nextContent() {
      if (this.quizId && this.quizId.length && !this.hasPassedQuiz) {
        return 'quiz'
      }
      if (this.nextVideo) {
        return 'video'
      }
      return ''
    },
    hasWatched() {
      return this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
    },
  },
  methods: {
    updateLoader(e) {
      this.$emit('isLoadingVideo', e)
    },
    playNextContent() {
      const { nextContent, quizId, nextVideo } = this
      switch (nextContent) {
        case 'quiz':
          this.$store.dispatch('content/setPlayingVideoId', '')
          this.$store.dispatch('content/setCurrentQuizId', quizId)
          break
        case 'video':
          this.$store.dispatch('content/setPlayingVideoTime', 0)
          this.$store.dispatch('content/setPlayingVideoId', nextVideo.uuid)
          break
        default:
          break
      }
    },
    async toggleVideoStatus() {
      this.isLoading = true
      const {
        video: { id: videoId },
        videoStatus,
        courseId,
      } = this
      const newStatus =
        videoStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
          ? CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
          : CONSTANTS.CONTENT_USER_STATUS.FINISHED
      await this.$store.dispatch('content/updateVideoStatus', {
        videoId,
        status: newStatus,
      })
      if (newStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED) {
        this.$store.dispatch('content/updateLegacyStatus', {
          userId: this.profile.content_api_id,
          videoId,
        })
      }
      await this.$store.dispatch('content/getVideoStatus', videoId)
      setTimeout(async () => {
        await this.$store.dispatch('courses/getCourseById', courseId)
      }, 500)
      this.isLoading = false
    },
    startIssue() {
      this.$store.dispatch('courses/saveContentIssue', {
        content_type: 'video',
        content_subtype: this.video.type,
        content_name: this.video.name,
        content_id: this.video.id,
        extra_args: {
          video_player_time: this.$refs.mediaDelegate.$refs.currentVideo
            .currentTime,
          video_player_status: this.videoStatus,
        },
      })
    },
  },
}
</script>

<style lang="scss">
.course-video {
  $this: &;
  margin: 0 auto 50px 0;
  transition: 1s all ease;
  position: relative;
  &__details {
    display: flex;
    align-items: center;
    margin: 20px 0 20px 0;
  }
  &__icon-wrapper {
    background: var(--light-gray);
    height: 62px;
    width: 62px;
    margin: 0 5px 0 0;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__loading-wrapper {
    width: 62px;
    height: 62px;
    text-align: center;
  }
  &__loading-spinner {
    margin: 5px 0 0 0;
  }
  &__icon {
    vertical-align: middle;
  }
  &__duration-wrapper {
    display: flex;
    align-items: center;
    margin: 5px 0 0 50px;
  }
  &__duration {
    color: var(--white);
  }
  &__player-wrapper {
    @include desktop {
      padding: 0 50px;
    }
  }
  .toggle-icons {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
    @include mobile {
      width: 100%;
      justify-content: initial;
    }
    &__check-wrapper {
      display: flex;
    }
    &__check {
      margin-left: 20px;
    }
    &__mark-as-finish {
      background: transparent;
      color: var(--white);
      font-weight: 700;
      border: none;
      padding: 0px;
      margin: 0px 5px 0 10px;
      cursor: pointer;
    }
  }
  &__report-issue {
    color: white;
    margin-left: auto;
    margin-right: 15px;
    display: flex;
    align-items: center;
    cursor: pointer;
    &__icon {
      fill: #717171;
      margin-right: 5px;
    }
  }
}
</style>
